/**
 * Rate Limiter - Controle de taxa de requisições no frontend
 * Previne spam de chamadas API e melhora performance
 */

class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = new Map(); // endpoint -> [timestamps]
  }

  /**
   * Verifica se a requisição pode ser executada
   * @param {string} key - Identificador único (ex: 'GET:/api/requisicoes')
   * @returns {object} { allowed: boolean, remaining: number, resetTime: number }
   */
  check(key) {
    const now = Date.now();
    const requestLog = this.requests.get(key) || [];
    
    // Remove timestamps antigos (fora da janela)
    const validRequests = requestLog.filter(timestamp => now - timestamp < this.windowMs);
    
    // Atualiza o log
    this.requests.set(key, validRequests);
    
    const remaining = Math.max(0, this.maxRequests - validRequests.length);
    const allowed = validRequests.length < this.maxRequests;
    
    // Calcula quando o rate limit será resetado
    const oldestRequest = validRequests[0];
    const resetTime = oldestRequest ? oldestRequest + this.windowMs : now;
    
    return {
      allowed,
      remaining,
      resetTime,
      retryAfter: allowed ? 0 : Math.ceil((resetTime - now) / 1000)
    };
  }

  /**
   * Registra uma nova requisição
   * @param {string} key - Identificador único
   * @returns {boolean} - true se permitido, false se bloqueado
   */
  consume(key) {
    const status = this.check(key);
    
    if (!status.allowed) {
      console.warn(`⚠️ Rate limit excedido para ${key}. Tente novamente em ${status.retryAfter}s`);
      return false;
    }
    
    const requestLog = this.requests.get(key) || [];
    requestLog.push(Date.now());
    this.requests.set(key, requestLog);
    
    return true;
  }

  /**
   * Reseta o rate limiter para uma chave específica
   * @param {string} key - Identificador único
   */
  reset(key) {
    this.requests.delete(key);
  }

  /**
   * Limpa todo o histórico de requisições
   */
  clear() {
    this.requests.clear();
  }

  /**
   * Obtém estatísticas do rate limiter
   */
  getStats() {
    const stats = {};
    const now = Date.now();
    
    for (const [key, timestamps] of this.requests.entries()) {
      const valid = timestamps.filter(ts => now - ts < this.windowMs);
      stats[key] = {
        count: valid.length,
        remaining: this.maxRequests - valid.length,
        resetIn: valid[0] ? Math.ceil((valid[0] + this.windowMs - now) / 1000) : 0
      };
    }
    
    return stats;
  }
}

// Instância global com limites padrão
export const globalRateLimiter = new RateLimiter(10, 60000); // 10 req/min

// Rate limiters específicos por tipo de operação
export const rateLimiters = {
  // Operações de leitura - mais permissivo
  read: new RateLimiter(30, 60000), // 30 req/min
  
  // Operações de escrita - mais restritivo
  write: new RateLimiter(10, 60000), // 10 req/min
  
  // Operações críticas (aprovar, finalizar) - muito restritivo
  critical: new RateLimiter(5, 60000), // 5 req/min
  
  // Busca/filtros - intermediário
  search: new RateLimiter(20, 60000), // 20 req/min
  
  // Upload de arquivos - muito restritivo
  upload: new RateLimiter(3, 60000), // 3 req/min
  
  // Notificações/emails - restritivo
  notification: new RateLimiter(5, 300000) // 5 req/5min
};

/**
 * Hook para usar rate limiter em componentes
 * @param {string} limiterType - Tipo de limiter: 'read', 'write', 'critical', etc
 * @returns {function} - Função para verificar rate limit
 */
export function useRateLimit(limiterType = 'read') {
  const limiter = rateLimiters[limiterType] || globalRateLimiter;
  
  return (key) => {
    const status = limiter.check(key);
    
    if (!status.allowed) {
      // Mostrar notificação ao usuário
      const message = `Muitas requisições. Aguarde ${status.retryAfter}s`;
      console.warn(`🚦 Rate limit: ${message}`);
      
      // Você pode integrar com o Toast aqui
      if (window.toast) {
        window.toast.warning(message);
      }
      
      return false;
    }
    
    return limiter.consume(key);
  };
}

/**
 * Decorator/Wrapper para adicionar rate limiting a funções
 * @param {function} fn - Função a ser rate limited
 * @param {string} key - Chave única
 * @param {string} limiterType - Tipo de limiter
 * @returns {function} - Função wrapped com rate limiting
 */
export function withRateLimit(fn, key, limiterType = 'read') {
  const limiter = rateLimiters[limiterType] || globalRateLimiter;
  
  return async function(...args) {
    if (!limiter.consume(key)) {
      const status = limiter.check(key);
      throw new Error(`Rate limit excedido. Tente novamente em ${status.retryAfter}s`);
    }
    
    return fn(...args);
  };
}

export default RateLimiter;
